/**
 * @module engine/export/exportPdf
 * @summary Export the drawing as a VECTOR, multi-sheet, scaled PDF sheet set.
 * @description
 *   This is the sheet a supplier actually receives, so it is built the way a
 *   drawing office would build it rather than the way a screenshot would.
 *
 *   **Vector, not a photograph.** The previous exporter rendered the diagram
 *   into an off-screen canvas at 3× and pasted the result onto the page as a
 *   JPEG. That has three problems on a real layout: JPEG puts grey haloes around
 *   every thin line, zooming past the rendered resolution goes soft exactly when
 *   a supplier is trying to read a nozzle, and a drawing wider than ~5,460 units
 *   asked the browser for a canvas over its 16,384 px limit and came out blank.
 *   Everything now goes through `PdfCanvas`, so the page holds real lines and
 *   real glyphs: sharp at any zoom, correct on a plotter, and smaller on disk.
 *
 *   **Plotted to a standard scale.** The layout is placed at 1:100, 1:200, … —
 *   the tightest ratio on the ladder that still fits the sheet — instead of
 *   being stretched to fill the paper. That, plus the scale bar, is what lets
 *   the reader measure something and get a real dimension back.
 *
 *   **A sheet set, not a sheet.** Sheet 1 is the layout. Sheet 2 onwards is the
 *   equipment schedule, because suppliers quote from a list.
 *
 *   **2D only.** The 3D view has its own export — see `export3dPdf` — and the two
 *   are deliberately different documents: this one is the measurable engineering
 *   drawing, that one is the visualisation. Mixing a 3D view into a sheet set
 *   that states a plot ratio invites a reader to measure the view, which is
 *   exactly what an isometric cannot support.
 *
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 3782-3897 (superseded).
 */
import { S } from '../state';
import { setCtx, drawFn, drawLabel } from '../shapes/drawFns';
import { drawConn } from '../routing';
import { computeBalance, streamMaterial, streamRateText } from '../balance';
import { toast } from '../../lib/toast';
import { dlText } from '../../lib/download';
import { PdfCanvas } from './pdf/PdfCanvas';
import {
  PAPERS,
  Sheet,
  pickScale,
  sheetGeometry,
  type PaperName,
  type TitleInfo,
} from './pdf/sheet';
import { buildSchedule, drawSchedule, scheduleCapacity, scheduleCsv } from './pdf/schedule';

export interface PdfExportOptions {
  paper?: PaperName;
  /** Landscape unless told otherwise — layouts are wider than they are deep. */
  orientation?: 'landscape' | 'portrait';
  /**
   * `'auto'` walks the standard ladder for the tightest fit. A number forces
   * that ratio, and the drawing is clipped to the frame if it will not fit —
   * an honest overflow beats silently re-scaling a drawing someone will measure.
   */
  scale?: 'auto' | number;
  /** `'fit'` abandons a standard ratio and fills the sheet, for a schematic PFD. */
  fit?: boolean;
  /** Printed in the title block, e.g. `'ISSUED FOR QUOTATION'`. */
  status?: string;
  /** Faint diagonal overprint, for a sheet nobody may build from. */
  watermark?: string;
  /** Bearing of north, degrees clockwise from up the sheet. */
  northDeg?: number;
  includeSchedule?: boolean;
  /** Also download the schedule as a CSV alongside the PDF. */
  scheduleCsvToo?: boolean;
}

const DEFAULTS: Required<Omit<PdfExportOptions, 'watermark'>> = {
  paper: 'A1',
  orientation: 'landscape',
  scale: 'auto',
  fit: false,
  status: 'ISSUED FOR QUOTATION',
  northDeg: 0,
  includeSchedule: true,
  scheduleCsvToo: false,
};

/** The drawing's extent in world units, padded for the things drawn outside it. */
function bounds(): { x: number; y: number; w: number; h: number } | null {
  if (!S.shapes.length) return null;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const s of S.shapes) {
    const fs = s.fontSize || 10;
    // Labels sit below the shape and tags sit 14 units above it, and both are
    // part of the drawing — the old exporter measured only the bodies and could
    // shave a tag off the top edge.
    const top = s.y - (s.tag ? 20 : 4);
    const bottom = s.y + s.h + (s.label ? fs + 6 : 4);
    x0 = Math.min(x0, s.x - 4);
    y0 = Math.min(y0, top);
    x1 = Math.max(x1, s.x + s.w + 4);
    y1 = Math.max(y1, bottom);
  }
  // A dragged connector waypoint can sit outside every shape.
  for (const cn of S.connections) {
    if (typeof cn.midX === 'number') {
      x0 = Math.min(x0, cn.midX - 6);
      x1 = Math.max(x1, cn.midX + 6);
    }
    if (typeof cn.midY === 'number') {
      y0 = Math.min(y0, cn.midY - 6);
      y1 = Math.max(y1, cn.midY + 6);
    }
  }
  if (!Number.isFinite(x0) || !Number.isFinite(y0)) return null;
  return { x: x0, y: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) };
}

/** Paint the drawing itself through the shim, in the order the screen uses. */
function paintDrawing(pc: PdfCanvas): void {
  const c = pc.asContext();
  const base = pc.depth();

  // Zone boxes are the backdrop, so they go down first and unsorted.
  for (const s of S.shapes) {
    if (s.type !== 'zonebox') continue;
    pc.save();
    try {
      drawFn(c, s);
    } catch {
      /* a broken custom symbol must not cost us the rest of the drawing */
    }
    pc.unwindTo(base);
  }

  // Same balance the screen shows: a plotted sheet that disagrees with the
  // drawing it came from is worse than one that carries no figures at all.
  const bal = computeBalance();
  for (const cn of S.connections) {
    pc.save();
    try {
      drawConn(c, cn, false, streamRateText(bal, cn.id), streamMaterial(bal, cn.id));
    } catch {
      /* skip an unroutable connector */
    }
    pc.unwindTo(base);
  }

  const ordered = S.shapes
    .filter((s) => s.type !== 'zonebox')
    .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

  for (const s of ordered) {
    pc.save();
    try {
      setCtx(c, s);
      if (s.rotation) {
        const cx = s.x + s.w / 2;
        const cy = s.y + s.h / 2;
        pc.translate(cx, cy);
        pc.rotate((s.rotation * Math.PI) / 180);
        pc.translate(-cx, -cy);
      }
      drawFn(c, s);
    } catch {
      /* skip a broken symbol */
    }
    pc.unwindTo(base);

    // Labels sit outside the rotation transform, exactly as render.ts does it,
    // so a rotated pump still has upright text.
    pc.save();
    try {
      setCtx(c, s);
      drawLabel(c, s);
    } catch {
      /* skip an unrenderable label */
    }
    pc.unwindTo(base);
  }
}

/** What an export WILL produce, worked out without building it. */
export interface ExportPlan {
  paper: PaperName;
  orientation: 'landscape' | 'portrait';
  /** Plot denominator; 0 means the sheet is marked NTS. */
  ratio: number;
  /** `'1:200'` or `'NTS'`. */
  scaleText: string;
  /** Page millimetres per drawing unit. */
  k: number;
  /** False when a forced scale will not fit and the drawing gets clipped. */
  fits: boolean;
  /** Real-world extent of the drawing, in metres. */
  realW: number;
  realH: number;
  scheduleRows: number;
  scheduleSheets: number;
  sheets: number;
  /** Where the drawing lands on sheet 1, in page millimetres. */
  place: { x: number; y: number; w: number; h: number };
  /** The plot rectangle it has to stay inside. */
  plot: { x: number; y: number; w: number; h: number };
}

/**
 * Work out the plot scale and sheet count without writing a PDF.
 *
 * The export dialog shows the user the scale they are about to get, and the
 * title block has to print "Sheet 1 of 3" on the first sheet — before the third
 * exists. Both read from here, so neither can drift from what actually gets
 * drawn.
 */
export function planExport(options: PdfExportOptions = {}): ExportPlan | null {
  const o = { ...DEFAULTS, ...options };
  const bb = bounds();
  if (!bb) return null;
  const paper = PAPERS[o.paper] ? o.paper : 'A1';
  const scaleMm = S.scaleMm > 0 ? S.scaleMm : 25;
  const g = sheetGeometry(paper, o.orientation);
  // Leave a little air so a symbol never touches the frame line.
  const availW = g.plot.w - 8;
  const availH = g.plot.h - 8;

  let ratio: number;
  let k: number;
  let scaleText: string;
  if (o.fit) {
    // A schematic PFD has no meaningful scale; say so rather than print a lie.
    k = Math.min(availW / bb.w, availH / bb.h);
    ratio = 0;
    scaleText = 'NTS';
  } else if (o.scale === 'auto') {
    ratio = Math.max(pickScale(bb.w * scaleMm, availW), pickScale(bb.h * scaleMm, availH));
    k = scaleMm / ratio;
    scaleText = `1:${ratio}`;
  } else {
    ratio = Math.max(1, Math.round(Number(o.scale) || 100));
    k = scaleMm / ratio;
    scaleText = `1:${ratio}`;
  }

  const levels = new Map(S.levels.map((l) => [l.id, l.name]));
  const rowCount = o.includeSchedule
    ? buildSchedule(S.shapes, scaleMm, (id) => (id ? levels.get(id) || '' : '')).length
    : 0;
  const scheduleSheets = rowCount ? Math.ceil(rowCount / scheduleCapacity(g.plot.h, true)) : 0;

  const drawnW = bb.w * k;
  const drawnH = bb.h * k;
  return {
    paper,
    orientation: o.orientation,
    ratio,
    scaleText,
    k,
    fits: drawnW <= availW + 0.5 && drawnH <= availH + 0.5,
    // Centred in the plot area — the exporter must place it here and nowhere else.
    place: {
      x: g.plot.x + (g.plot.w - drawnW) / 2,
      y: g.plot.y + (g.plot.h - drawnH) / 2,
      w: drawnW,
      h: drawnH,
    },
    plot: g.plot,
    realW: (bb.w * scaleMm) / 1000,
    realH: (bb.h * scaleMm) / 1000,
    scheduleRows: rowCount,
    scheduleSheets,
    sheets: 1 + scheduleSheets,
  };
}

/** A finished, unsaved sheet set plus what went into deciding its shape. */
export interface BuiltSheetSet {
  doc: import('jspdf').jsPDF;
  plan: ExportPlan;
  rows: ReturnType<typeof buildSchedule>;
  /** Filename stem, without an extension. */
  stem: string;
}

/**
 * Build the whole sheet set and hand it back UNSAVED.
 *
 * Kept separate from `exportPdf` for one reason: downloading needs a browser,
 * and the self-check needs to open the finished document and read its content
 * stream back. Every line that decides what the drawing looks like therefore
 * lives on this side of the boundary, where it can be tested.
 */
export async function buildSheetSet(
  jsPDFCtor: typeof import('jspdf').jsPDF,
  options: PdfExportOptions = {},
): Promise<BuiltSheetSet | null> {
  const o = { ...DEFAULTS, ...options };
  const name = S.docMeta.docName || 'PFD';
  const bb = bounds();
  if (!bb) return null;

  const paper = PAPERS[o.paper] ? o.paper : 'A1';
  const doc = new jsPDFCtor({
    orientation: o.orientation,
    unit: 'mm',
    format: paper.toLowerCase(),
    compress: true,
  });
  doc.setProperties({
    title: `${name} — Plant Layout`,
    subject: o.status,
    author: S.docMeta.drawnBy || 'Jeddah Cables Company',
    creator: 'JCC PFD Engineering Tool',
  });

  const scaleMm = S.scaleMm > 0 ? S.scaleMm : 25;
  const levels = new Map(S.levels.map((l) => [l.id, l.name]));
  const rows = o.includeSchedule
    ? buildSchedule(S.shapes, scaleMm, (id) => (id ? levels.get(id) || '' : ''))
    : [];

  // The scale and the sheet count come from the planner, so the title block's
  // "Sheet 1 of 3" is settled before the first line is drawn.
  const plan = planExport(options);
  if (!plan) return null;
  const layoutSheet = new Sheet(doc, paper, o.orientation);
  const schedSheets = plan.scheduleSheets;
  const total = plan.sheets;

  const title = (sheetTitle: string, sheetNo: number, scaleText: string): TitleInfo => ({
    docName: name,
    rev: S.docMeta.rev,
    date: S.docMeta.date,
    drawnBy: S.docMeta.drawnBy,
    approvedBy: S.docMeta.approvedBy,
    status: o.status,
    sheetTitle,
    sheetNo,
    sheetCount: total,
    scaleText,
    paper,
  });

  // ═══════════════ Sheet 1 — the layout ═══════════════
  const plot = layoutSheet.plot;
  const { k, ratio, scaleText } = plan;

  layoutSheet.drawFrame();

  const pc = new PdfCanvas(doc, { minLineMm: 0.13, minTextMm: 1.4 });
  // Centre the drawing in the plot area, then clip to it so a forced scale that
  // overflows spills onto the floor rather than over the frame and title block.
  pc.save();
  pc.beginPath();
  pc.rect(plot.x, plot.y, plot.w, plot.h);
  pc.clip();
  pc.setTransform(k, 0, 0, k, plan.place.x - bb.x * k, plan.place.y - bb.y * k);

  // Dash patterns in `drawConn` are divided by the live zoom so they look even
  // on screen. On paper there is no zoom, so pin it to 1 for the duration.
  const zoomWas = S.zoom;
  S.zoom = 1;
  try {
    paintDrawing(pc);
  } finally {
    S.zoom = zoomWas;
    pc.unwindTo(0);
  }

  // ── Furniture, drawn over the drawing so nothing can bury it ──
  layoutSheet.drawFurniture(ratio, o.northDeg, o.fit);
  if (o.watermark) layoutSheet.drawWatermark(o.watermark);
  layoutSheet.drawTitleBlock(title('PLANT LAYOUT', 1, scaleText));

  // ═══════════════ Sheets 2… — the equipment schedule ═══════════════
  let cursor = 0;
  for (let i = 0; i < schedSheets; i++) {
    doc.addPage(paper.toLowerCase(), o.orientation);
    const sh = new Sheet(doc, paper, o.orientation);
    sh.drawFrame();
    const before = cursor;
    cursor = drawSchedule(
      doc,
      sh.plot,
      rows,
      cursor,
      'Sizes are the plan footprint taken from the layout and are approximate — confirm against vendor drawings before fabrication.',
    );
    if (o.watermark) sh.drawWatermark(o.watermark);
    sh.drawTitleBlock(
      title(
        schedSheets > 1 ? `EQUIPMENT SCHEDULE (${i + 1}/${schedSheets})` : 'EQUIPMENT SCHEDULE',
        2 + i,
        'NTS',
      ),
    );
    // A schedule that stops advancing would loop forever; bail loudly instead.
    if (cursor === before) break;
  }

  const safe = (name || 'PFD').replace(/[^a-zA-Z0-9_\- ]/g, '').trim() || 'PFD';
  return { doc, plan, rows, stem: `JCC_${safe}_Rev${S.docMeta.rev || 'A'}` };
}

/** Build the sheet set and download it (plus the schedule CSV, if asked). */
export async function exportPdf(options: PdfExportOptions = {}): Promise<void> {
  let jsPDFCtor: typeof import('jspdf').jsPDF;
  try {
    jsPDFCtor = (await import('jspdf')).jsPDF;
  } catch {
    toast('Could not load the PDF writer. Reload the page and try again.');
    return;
  }

  const built = await buildSheetSet(jsPDFCtor, options);
  if (!built) {
    toast('Nothing on the canvas to export.');
    return;
  }
  const { doc, plan, rows, stem } = built;
  doc.save(`${stem}.pdf`);

  if (options.scheduleCsvToo && rows.length) {
    dlText(scheduleCsv(rows), `${stem}_equipment.csv`, 'text/csv;charset=utf-8');
  }

  if (!plan.fits) {
    toast(
      `Plotted at ${plan.scaleText}, which is larger than ${plan.paper} — the drawing is clipped. ` +
        'Use a coarser scale or a bigger sheet.',
      6000,
    );
  } else {
    toast(
      `${plan.sheets} sheet${plan.sheets > 1 ? 's' : ''} exported — ${plan.scaleText} on ${plan.paper}.`,
      3500,
    );
  }
}
